import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PackagePlus, User as UserIcon, LayoutDashboard, PackageCheck, Heart, Bell, Boxes, Check, MessageSquare, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import OrderTimeline from "@/components/OrderTimeline";
import STLViewer from "@/components/STLViewer";

const uploadUrl = (p) => (p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`);
const isGlb = (p) => (p || "").toLowerCase().endsWith(".glb");
const isStl = (p) => (p || "").toLowerCase().endsWith(".stl");
const is3mf = (p) => (p || "").toLowerCase().endsWith(".3mf");

const TABS = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "orders", label: "Orders", icon: PackageCheck },
    { key: "wishlist", label: "Wishlist", icon: Heart },
    { key: "profile", label: "Profile", icon: UserIcon },
];

const CustomerDashboard = () => {
    const { user } = useAuth();
    const location = useLocation();
    const initialTab = location.pathname.includes("wishlist") ? "wishlist" : location.pathname.includes("orders") ? "orders" : "overview";
    const [tab, setTab] = useState(initialTab);
    const [orders, setOrders] = useState([]);
    const [wishlist, setWishlist] = useState([]);

    const load = async () => {
        const [o, w] = await Promise.all([
            api.get("/orders/mine").catch(() => ({ data: { orders: [] } })),
            api.get("/wishlist").catch(() => ({ data: { wishlist: [] } })),
        ]);
        setOrders(o.data.orders);
        setWishlist(w.data.wishlist);
    };
    useEffect(() => { load(); }, []);

    const previewCount = orders.reduce((n, o) => n + (o.items || []).filter((it) => (it.production_files || []).length).length, 0);
    const awaiting = orders.reduce((n, o) => n + (o.items || []).filter((it) => (it.production_files || []).length && !["approved", "revision_requested"].includes(it.approval_status || "pending")).length, 0);

    if (!user) return null;

    return (
        <div className="max-w-7xl mx-auto px-6 py-10" data-testid="customer-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Welcome back, {user.name?.split(" ")[0]}</div>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-1">Your dashboard</h1>
                </div>
                <Link to="/shop"><button className="rounded-full h-11 px-5 bg-primary text-primary-foreground text-sm font-semibold flex items-center gap-2"><PackagePlus className="w-4 h-4" /> New miniature</button></Link>
            </div>

            <div className="mt-8 grid lg:grid-cols-[220px_1fr] gap-6">
                <aside className="rounded-3xl glass p-2 h-fit sticky top-28">
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setTab(t.key)} data-testid={`dashboard-tab-${t.key}`}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-2xl text-sm ${tab === t.key ? "bg-primary/10 text-primary font-semibold" : "hover:bg-primary/5"}`}>
                            <t.icon className="w-4 h-4" /> {t.label}
                        </button>
                    ))}
                </aside>

                <div>
                    {tab === "overview" && (
                        <div className="grid md:grid-cols-3 gap-4">
                            <StatCard title="Orders" value={orders.length} icon={PackageCheck} />
                            <StatCard title="Wishlist" value={wishlist.length} icon={Heart} />
                            <StatCard title="3D previews" value={previewCount} icon={Boxes} hint={awaiting ? `${awaiting} awaiting approval` : null} />
                            <div className="md:col-span-3 rounded-3xl glass p-6">
                                <h3 className="font-display text-lg font-semibold mb-4">Recent orders</h3>
                                {orders.length === 0 ? (
                                    <div className="text-sm text-muted-foreground">No orders yet.</div>
                                ) : (
                                    <div className="space-y-3">
                                        {orders.slice(0, 3).map((o) => <OrderRow key={o.id} order={o} onChanged={load} />)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {tab === "orders" && (
                        <div className="rounded-3xl glass p-6">
                            <h3 className="font-display text-lg font-semibold mb-4">All orders</h3>
                            {orders.length === 0 ? <EmptyState label="No orders yet — start creating your first miniature." /> : (
                                <div className="space-y-4">{orders.map((o) => <OrderRow key={o.id} order={o} expanded onChanged={load} />)}</div>
                            )}
                        </div>
                    )}

                    {tab === "wishlist" && (
                        <div className="rounded-3xl glass p-6">
                            <h3 className="font-display text-lg font-semibold mb-4">Wishlist</h3>
                            {wishlist.length === 0 ? <EmptyState label="Save your favourites for later." /> : (
                                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                                    {wishlist.map((w) => (
                                        <Link key={w.id} to={`/product/${w.product?.slug}`} className="rounded-2xl bg-white/60 p-3">
                                            <div className="aspect-[4/5] rounded-xl overflow-hidden bg-brand-cream"><img src={w.product?.images?.[0]} alt="" className="w-full h-full object-cover" /></div>
                                            <div className="text-sm font-semibold mt-2">{w.product?.name}</div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "profile" && (
                        <div className="rounded-3xl glass p-6">
                            <h3 className="font-display text-lg font-semibold mb-4">Profile</h3>
                            <ProfileField label="Name" value={user.name} />
                            <ProfileField label="Email" value={user.email} />
                            <ProfileField label="Phone" value={user.phone || "—"} />
                            <ProfileField label="Role" value={user.role} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ title, value, icon: Icon, hint }) => (
    <div className="rounded-3xl glass p-5">
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div>
            <div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest">{title}</div>
                <div className="font-display text-3xl font-bold">{value}</div>
                {hint && <div className="text-[11px] text-amber-700 font-semibold">{hint}</div>}
            </div>
        </div>
    </div>
);

const OrderRow = ({ order, expanded, onChanged }) => (
    <div className="rounded-2xl bg-white/60 p-4" data-testid={`dashboard-order-${order.order_number}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
                <div className="text-xs uppercase text-muted-foreground">Order</div>
                <div className="font-semibold">{order.order_number}</div>
            </div>
            <div className="text-sm">{formatINR(order.total_inr)}</div>
            <span className={`text-[11px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full ${order.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-yellow-100 text-yellow-700"}`}>{order.payment_status}</span>
            <Link to={`/track?number=${order.order_number}`} className="text-xs text-primary">Track →</Link>
        </div>
        {expanded && (
            <div className="mt-4 border-t border-border pt-4">
                <OrderTimeline currentStage={order.status} history={order.timeline || []} />
            </div>
        )}
        {(order.items || []).some((it) => (it.production_files || []).length) && (
            <div className="mt-4 space-y-3" data-testid={`dashboard-previews-${order.order_number}`}>
                {order.items.map((it, idx) => (it.production_files || []).length ? (
                    <ItemPreview key={idx} order={order} item={it} idx={idx} onChanged={onChanged} />
                ) : null)}
            </div>
        )}
    </div>
);

const pickMesh = (files) => {
    const r = [...files].reverse();
    return r.find(is3mf) || r.find(isGlb) || r.find(isStl) || null;
};

const ItemPreview = ({ order, item, idx, onChanged }) => {
    const [busy, setBusy] = useState(null);
    const mesh = pickMesh(item.production_files || []);
    const status = item.approval_status || "pending";
    const act = async (kind) => {
        let body = {};
        if (kind === "revise") {
            const note = window.prompt("Describe the changes you'd like (pose, colour, expression…):");
            if (!note) return;
            body = { note };
        }
        setBusy(kind);
        try {
            await api.post(`/orders/mine/${order.id}/items/${idx}/${kind}`, body);
            toast.success(kind === "approve" ? "Preview approved — we'll begin production!" : "Change request sent!");
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(null); }
    };
    return (
        <div className="rounded-2xl border bg-white/70 p-3" data-testid={`dashboard-item-preview-${order.order_number}-${idx}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-semibold inline-flex items-center gap-1.5"><Boxes className="w-4 h-4 text-primary" /> {item.product_name} <span className="text-xs text-muted-foreground font-normal">· {item.size_label}</span></div>
                <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${status === "approved" ? "bg-emerald-100 text-emerald-700" : status === "revision_requested" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`} data-testid={`dashboard-approval-status-${order.order_number}-${idx}`}>
                    {status === "approved" ? "Approved" : status === "revision_requested" ? "Changes requested" : "Awaiting your approval"}
                </span>
            </div>
            {mesh && (
                <div className="mt-3 rounded-xl overflow-hidden bg-gradient-to-br from-brand-cream to-brand-lavender relative" style={{ height: 260 }}>
                    <STLViewer src={uploadUrl(mesh)} height={260} testId={`dashboard-viewer-${order.order_number}-${idx}`} />
                    {is3mf(mesh) && <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-white/70 text-brand-gold font-semibold pointer-events-none">Colour preview (3MF)</div>}
                </div>
            )}
            <div className="mt-2 flex flex-wrap gap-2 items-center">
                {(item.production_files || []).map((p, fi) => (
                    <a key={fi} href={uploadUrl(p)} target="_blank" rel="noreferrer" className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 text-primary inline-flex items-center gap-1 hover:bg-primary/20" data-testid={`dashboard-file-${order.order_number}-${idx}-${fi}`}>
                        <Download className="w-3 h-3" /> {p.split("/").pop()}
                    </a>
                ))}
            </div>
            {status !== "approved" && status !== "revision_requested" && (
                <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => act("approve")} disabled={!!busy} data-testid={`dashboard-approve-${order.order_number}-${idx}`} className="text-xs px-4 h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-50">
                        {busy === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve preview
                    </button>
                    <button onClick={() => act("revise")} disabled={!!busy} data-testid={`dashboard-request-changes-${order.order_number}-${idx}`} className="text-xs px-4 h-9 rounded-full border border-primary/40 text-primary hover:bg-primary/5 inline-flex items-center gap-1 disabled:opacity-50">
                        <MessageSquare className="w-3 h-3" /> Request changes
                    </button>
                </div>
            )}
            {status === "revision_requested" && item.revision_note && (
                <div className="mt-2 rounded-xl bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900"><b>Your note:</b> "{item.revision_note}" — a revised preview is on its way.</div>
            )}
        </div>
    );
};

const ProfileField = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-border/50 last:border-none">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-sm font-medium">{value}</span>
    </div>
);

const EmptyState = ({ label }) => (
    <div className="p-10 text-center text-sm text-muted-foreground">
        <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
        {label}
    </div>
);

export default CustomerDashboard;
